/**
 * runMap — the Run map's rules, kept out of the components so Vitest can cover them.
 */

import { lineColour } from './lineColour';
import type { APILoadingStatus } from '@vis.gl/react-google-maps';
import type { LineColour } from './lineColour';
import type { RunsheetLine } from '@/types';

/**
 * The bottom of the map that Google's logo and copyright strip sit in. No overlay,
 * sheet, card or button of ours enters it (the Run map paragraph in `responsive.md`).
 */
export const MAP_CLEAR_BAND = 32;

/** How far our controls on the map (zoom buttons, the not-on-map button) sit from its edges. */
export const MAP_CONTROL_INSET = 8;

/** Our map controls and every marker target but NEXT's are 44px, the Touch floor. */
export const TOUCH_FLOOR = 44;

/** Below this Google zoom, units at one address draw as one counted dot. */
export const FAN_ZOOM = 16;

// The visible circle of each kind of marker, and of the selected stop unless it is NEXT.
const MARKER_SIZES: Record<MarkerKind, number> = {
  next: 50,
  stop: 32,
  completed: 28,
  unordered: 32,
};
const SELECTED_SIZE = 44;

// Half a NEXT marker and an 8px gap, so a fitted marker shows whole.
const FIT_MARKER_CLEARANCE = MARKER_SIZES.next / 2 + 8;

/**
 * The first-open fit's padding, which keeps every point clear of the not-on-map button
 * at the top left, the zoom buttons at the right and the clear band at the bottom.
 */
export const FIT_PADDING = {
  top: MAP_CONTROL_INSET + TOUCH_FLOOR + FIT_MARKER_CLEARANCE,
  right: MAP_CONTROL_INSET + TOUCH_FLOOR + FIT_MARKER_CLEARANCE,
  bottom: MAP_CLEAR_BAND + FIT_MARKER_CLEARANCE,
  left: FIT_MARKER_CLEARANCE,
};

// Draw order, bottom to top: run line, you are here, completed stops, other stops
// (earlier run order higher), NEXT, transfer stations, the selected stop.
export const YOU_ARE_HERE_Z_INDEX = 1;
const ORDER_SPAN = 10_000;
const COMPLETED_Z_INDEX = 2;
const STOP_Z_INDEX = COMPLETED_Z_INDEX + ORDER_SPAN;
const NEXT_Z_INDEX = STOP_Z_INDEX + ORDER_SPAN;
export const STATION_Z_INDEX = NEXT_Z_INDEX + 1;
const SELECTED_Z_INDEX = STATION_Z_INDEX + 1;

// Any fanned-out unit can be NEXT, so the ring keeps 8px between NEXT-sized targets.
const FAN_TARGET = MARKER_SIZES.next;
const FAN_GAP = 8;
// Long enough that the leader lines show between the address and each unit.
const MIN_FAN_RADIUS = 36;

interface LatLng {
  lat: number;
  lng: number;
}

export interface Offset {
  x: number;
  y: number;
}

export type MapSetUp = 'ready' | 'no-signal' | 'not-set-up';

interface MapSetUpInput {
  apiKey: string | undefined;
  loadStatus: APILoadingStatus;
  /** Google called `window.gm_authFailure`, so it rejected the key. */
  authFailed: boolean;
}

/**
 * Whether the map can show. A rejected key is not set up, even if the load fails as
 * well, because Retry can't fix it. vis.gl 1.10.1 never sets `AUTH_FAILURE` itself,
 * so `authFailed` carries Google's own callback.
 */
export function mapSetUp({ apiKey, loadStatus, authFailed }: MapSetUpInput): MapSetUp {
  if (!apiKey || authFailed || loadStatus === 'AUTH_FAILURE') return 'not-set-up';
  if (loadStatus === 'FAILED') return 'no-signal';
  return 'ready';
}

// ── Positions ─────────────────────────────────────────────────────────────────

// The API encodes with JSON_NUMERIC_CHECK, so a coordinate typed as a string can arrive
// as a number, and a missing one as null.
function coordinate(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Where a stop or station is, or null when its coordinates aren't usable: blank, not a
 * number, or exactly 0,0, which is where a failed geocode leaves a stop.
 */
export function stopPosition(item: { lat: string; lon: string }): LatLng | null {
  const lat = coordinate(item.lat);
  const lng = coordinate(item.lon);
  if (lat === null || lng === null || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function byRunOrder(a: RunsheetLine, b: RunsheetLine): number {
  return a.displayorder - b.displayorder;
}

/** The stops the "N stops not on the map" list holds, in run order. */
export function stopsNotOnMap(lines: RunsheetLine[]): RunsheetLine[] {
  return lines.filter(line => !stopPosition(line)).sort(byRunOrder);
}

// ── Run line and NEXT ─────────────────────────────────────────────────────────

/**
 * The stops the run line passes through, in run order: on the map, not completed, not
 * gray, and with a run order above 0.
 */
export function runLineStops(lines: RunsheetLine[]): RunsheetLine[] {
  return lines
    .filter(
      line =>
        line.displayorder > 0 &&
        stopPosition(line) &&
        !['green', 'gray'].includes(lineColour(line)),
    )
    .sort(byRunOrder);
}

/** The run line's points. It starts at the driver, or at its first stop with no fix. */
export function runLinePath(lines: RunsheetLine[], driver: LatLng | null): LatLng[] {
  const stops = runLineStops(lines).map(line => stopPosition(line)!);
  return driver ? [driver, ...stops] : stops;
}

/** NEXT is the first stop on the run line, so a gray stop is never NEXT. */
export function nextStopId(lines: RunsheetLine[]): number | null {
  return runLineStops(lines)[0]?.id ?? null;
}

// ── Marker looks ──────────────────────────────────────────────────────────────

export type MarkerKind = 'next' | 'stop' | 'completed' | 'unordered';

export interface MarkerLook {
  kind: MarkerKind;
  colour: LineColour;
  /** The visible circle's diameter in px. Every marker but NEXT sits in a 44px target. */
  size: number;
  /** The selected stop's 4px dark ring. */
  ring: boolean;
}

interface MarkerState {
  nextId: number | null;
  selected: boolean;
}

function markerKind(line: RunsheetLine, nextId: number | null): MarkerKind {
  if (line.id === nextId) return 'next';
  if (line.driver_locked) return 'completed';
  if (line.displayorder <= 0) return 'unordered';
  return 'stop';
}

/** How a stop's marker looks. A selected NEXT keeps its 50px and gets the ring. */
export function markerLook(line: RunsheetLine, { nextId, selected }: MarkerState): MarkerLook {
  const kind = markerKind(line, nextId);
  const size = selected && kind !== 'next' ? SELECTED_SIZE : MARKER_SIZES[kind];
  return { kind, colour: lineColour(line), size, ring: selected };
}

/** A stop marker's `zIndex`. Every marker sets one, so the draw order never falls to Google. */
export function markerZIndex(line: RunsheetLine, { nextId, selected }: MarkerState): number {
  if (selected) return SELECTED_Z_INDEX;
  const kind = markerKind(line, nextId);
  if (kind === 'next') return NEXT_Z_INDEX;
  const order = Math.min(Math.max(line.displayorder, 0), ORDER_SPAN - 1);
  return (kind === 'completed' ? COMPLETED_Z_INDEX : STOP_Z_INDEX) + ORDER_SPAN - 1 - order;
}

/** A stop marker's accessible name, such as "Stop 12, 22 Latrobe Tce, Paddington". */
export function stopName(line: RunsheetLine): string {
  const stop = line.displayorder > 0 ? `Stop ${line.displayorder}` : 'New stop';
  return `${stop}, ${line.deliveryaddress}`;
}

/** A transfer station's short label, such as "Nudgee". */
export function stationLabel(name: string): string {
  return name.replace(/\s*transfer station$/i, '');
}

// ── Units at one address ──────────────────────────────────────────────────────

export function isFanned(zoom: number): boolean {
  return zoom >= FAN_ZOOM;
}

export interface MarkerGroup {
  key: string;
  position: LatLng;
  /** The stops at this position, in run order. A group of one is a single stop. */
  lines: RunsheetLine[];
  /** The unit the collapsed dot shows: the first not yet done, or the first. */
  lead: RunsheetLine;
  /** The collapsed dot's accessible name, such as "4 units, 22 Latrobe Tce, Paddington". */
  name: string;
}

// The comma-separated parts every address ends with, such as "22 Latrobe Tce, Paddington"
// for "Unit 1, 22 Latrobe Tce, Paddington" and "Unit 2, 22 Latrobe Tce, Paddington".
function sharedAddress(lines: RunsheetLine[]): string {
  const [first, ...others] = lines.map(line => line.deliveryaddress.split(',').map(p => p.trim()));
  let shared = first!;
  for (const parts of others) {
    let count = 0;
    while (
      count < Math.min(shared.length, parts.length) &&
      shared[shared.length - 1 - count] === parts[parts.length - 1 - count]
    ) {
      count++;
    }
    shared = shared.slice(shared.length - count);
  }
  // Only a suburb in common doesn't name the address, so the first unit's does.
  return shared.length > 1 ? shared.join(', ') : lines[0]!.deliveryaddress;
}

/**
 * The stops on the map, grouped by identical coordinates (units at one address), in the
 * run order of each group's first stop. Close neighbours at different coordinates stay
 * apart and overlap.
 */
export function markerGroups(lines: RunsheetLine[]): MarkerGroup[] {
  const groups = new Map<string, { position: LatLng; lines: RunsheetLine[] }>();
  for (const line of [...lines].sort(byRunOrder)) {
    const position = stopPosition(line);
    if (!position) continue;
    const key = `${position.lat},${position.lng}`;
    const group = groups.get(key) ?? { position, lines: [] };
    group.lines.push(line);
    groups.set(key, group);
  }
  return [...groups].map(([key, { position, lines: units }]) => ({
    key,
    position,
    lines: units,
    lead: units.find(line => !line.driver_locked) ?? units[0]!,
    name: `${units.length} units, ${sharedAddress(units)}`,
  }));
}

function fanAt(count: number, radius: number): Offset[] {
  // Start half a step round from the top, so two units sit side by side and four on
  // the diagonals, where their square targets are furthest apart.
  const start = -Math.PI / 2 + Math.PI / count;
  return Array.from({ length: count }, (_, i) => {
    const angle = start + (2 * Math.PI * i) / count;
    return { x: Math.round(radius * Math.cos(angle)), y: Math.round(radius * Math.sin(angle)) };
  });
}

function targetsClear(offsets: Offset[]): boolean {
  return offsets.every((a, i) =>
    offsets
      .slice(i + 1)
      .every(b => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) >= FAN_TARGET + FAN_GAP),
  );
}

/**
 * How a collapsed group's dot looks: the size of a pending stop, in its lead unit's
 * colour, with the ring when one of its units is selected.
 */
export function unitsDotLook(group: MarkerGroup, selected: boolean): MarkerLook {
  return { kind: 'stop', colour: lineColour(group.lead), size: MARKER_SIZES.stop, ring: selected };
}

/**
 * Where each fanned-out unit's target sits, in px from the address, on a ring just wide
 * enough to keep every target 8px from the next.
 */
export function fanOffsets(count: number): Offset[] {
  let radius = MIN_FAN_RADIUS;
  while (!targetsClear(fanAt(count, radius))) radius++;
  return fanAt(count, radius);
}

// ── First-open fit ────────────────────────────────────────────────────────────

/**
 * The points the first open fits: every stop not yet done that has coordinates (gray
 * and run order 0 included), or every stop when none is left, plus the driver however
 * far away. Stations are left out.
 */
export function fitPoints(lines: RunsheetLine[], driver: LatLng | null): LatLng[] {
  const remaining = lines.filter(line => !line.driver_locked && stopPosition(line));
  const stops = (remaining.length > 0 ? remaining : lines).flatMap(
    line => stopPosition(line) ?? [],
  );
  return driver ? [...stops, driver] : stops;
}

/** The smallest box holding every point, for `fitBounds`. */
export function boundsOf(points: LatLng[]): {
  north: number;
  south: number;
  east: number;
  west: number;
} {
  const lats = points.map(point => point.lat);
  const lngs = points.map(point => point.lng);
  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs),
  };
}

// ── Pan clear ─────────────────────────────────────────────────────────────────

// A tapped marker this close to the sheet or card counts as under it.
const PAN_CLEAR_MARGIN = 60;

/** A box in px from the map's top left corner. */
export interface MapRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How far to pan the map (`panBy`) so a tapped marker at `point` sits in the middle of
 * the part of the map the sheet leaves open: the band above it in Single-pane, the area
 * right of the card in Split. Null when the marker is more than 60px from the sheet,
 * so a marker already clear leaves the camera alone.
 */
export function panClearBy(
  point: Offset,
  map: { width: number; height: number },
  sheet: MapRect,
  split: boolean,
): Offset | null {
  const near =
    point.x >= sheet.left - PAN_CLEAR_MARGIN &&
    point.x <= sheet.right + PAN_CLEAR_MARGIN &&
    point.y >= sheet.top - PAN_CLEAR_MARGIN &&
    point.y <= sheet.bottom + PAN_CLEAR_MARGIN;
  if (!near) return null;

  const open = split
    ? { left: sheet.right, top: 0, right: map.width, bottom: map.height }
    : { left: 0, top: 0, right: map.width, bottom: sheet.top };
  return {
    x: point.x - (open.left + open.right) / 2,
    y: point.y - (open.top + open.bottom) / 2,
  };
}
